import { useState } from 'react';
import { cn } from '@/lib/utils';
import { BottomSheet } from '@/components/ui';
import { useAppAuth } from '@/lib/useAppAuth';
import { centsToPercent } from '@/lib/binary-market';
import { SnakeBuyPanel, type SnakeBuyPanelProps } from './SnakeBuyPanel';

/**
 * Floating Up/Down trade bar for phones (<md), where the inline buy panel /
 * desktop rail isn't shown. A docked bar sits above the bottom menu (or the
 * viewport bottom for signed-out visitors, who have no bottom nav); tapping
 * either side opens a bottom-sheet drawer with the full Set Amount panel — the
 * same stepper, quick-add cubes, and Up/Down buy buttons as the desktop rail.
 *
 * The bar itself never places an order — it is an entry point to the drawer,
 * so every buy keeps going through {@link SnakeBuyPanel}'s guards (kill-switch,
 * territory gate, price-known checks).
 */
export function SnakeTradeBar(props: SnakeBuyPanelProps) {
  const [open, setOpen] = useState(false);
  const { isAuthenticated } = useAppAuth();

  const upPct = centsToPercent(props.upCents);
  const downPct = centsToPercent(props.downCents);

  return (
    <>
      {/* Docked action bar — its own opaque surface + blur + top border so page
          content scrolling underneath never shows through the button gap. */}
      <div
        className={cn(
          'fixed inset-x-0 z-40 border-t border-border bg-surface/95 px-4 pt-3 backdrop-blur-md md:hidden',
          // Above the mobile bottom nav (82px + safe area) when signed in;
          // guests have no nav, so the bar sits at the viewport bottom.
          isAuthenticated
            ? 'bottom-[82px] pb-[calc(env(safe-area-inset-bottom)+12px)]'
            : 'bottom-0 pb-[calc(env(safe-area-inset-bottom)+12px)]',
        )}
      >
        <div className="mx-auto flex max-w-[608px] gap-3">
          <TradeBarButton side="up" label="Up" percent={upPct} onClick={() => setOpen(true)} />
          <TradeBarButton side="down" label="Down" percent={downPct} onClick={() => setOpen(true)} />
        </div>
      </div>

      <BottomSheet open={open} onClose={() => setOpen(false)}>
        {/* The drawer hosts the exact Set Amount panel; its Card chrome doubles
            as the sheet body. */}
        <SnakeBuyPanel {...props} />
      </BottomSheet>
    </>
  );
}

/** One side of the floating bar — same gradient pill language as the buy panel. */
function TradeBarButton({
  side,
  label,
  percent,
  onClick,
}: {
  side: 'up' | 'down';
  label: string;
  percent: number | null;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        side === 'up' ? 'bg-gradient-up' : 'bg-gradient-down',
        'shadow-btn-strong flex h-[50px] flex-1 items-center justify-center gap-1.5 rounded-[51px] text-white active:opacity-80',
      )}
    >
      <span className="text-[20px] font-semibold">{label}</span>
      <span className="text-[14px] font-semibold">{percent == null ? '—' : `${percent}%`}</span>
    </button>
  );
}
